import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import cloneDeep from 'lodash/cloneDeep.js';
import isEqual from 'lodash/isEqual.js';
import get from 'lodash/get.js';
import keyBy from 'lodash/keyBy.js';
import scrollParent from 'scrollparent';
import { ObserveVisibility } from 'vue-observe-visibility';
import ResizeObserver from 'resize-observer-polyfill';
import PendoTooltip from '../../directives/tooltip/pendo-tooltip.js';
import { PendoLoading } from '../../directives/loading/pendo-loading.js';
import PendoCollapseTransition from '../../utils/pendo-collapse-transition.vue.js';
import TableTitle from './table-title.vue.js';
import TableHeader from './table-header.vue.js';
import TableBody from './table-body.vue.js';
import PendoColumnChooser from './column-chooser/pendo-column-chooser.vue.js';
import { getCSVColumns, getCSVFilename, tableToCSV, downloadCSV } from './utils/csv.js';
import { emitEvent, getColumnConfig, flattenChildRows, findRowLocation } from './utils/utils.js';
import { hasSlot, getShallowObjectDiff } from '../../utils/utils.js';
import { elementPositionInAncestor, getScrollParent, getScrollbarWidth, removeClass, addClass } from '../../utils/dom.js';
import { warn } from '../../utils/console.js';
import { sortRows } from './utils/sort.js';
import { filterRows } from './utils/filter.js';
import { groupRows } from './utils/group.js';

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }
  if (!value || !value.then) {
    value = Promise.resolve(value);
  }
  return then ? value.then(then) : value;
}
function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }
    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}
function _invoke(body, then) {
  var result = body();
  if (result && result.then) {
    return result.then(then);
  }
  return then(result);
}
var script = {
  name: 'PendoTable',
  components: {
    PendoCollapseTransition,
    PendoColumnChooser,
    TableTitle,
    TableHeader,
    TableBody
  },
  directives: {
    ObserveVisibility,
    PendoLoading,
    PendoTooltip
  },
  provide() {
    return {
      $table: this
    };
  },
  inheritAttrs: false,
  props: {
    columns: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    height: {
      type: [Number, String],
      default: null
    },
    maxHeight: {
      type: [Number, String],
      default: null
    },
    resizable: {
      type: Boolean,
      default: false
    },
    draggable: {
      type: Boolean,
      default: false
    },
    stripe: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: false
    },
    condensed: {
      type: Boolean,
      default: false
    },
    fit: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: null
    },
    status: {
      type: String,
      default: null
    },
    highlightCurrentRow: {
      type: Boolean,
      default: false
    },
    highlightHoverRow: {
      type: Boolean,
      default: true
    },
    rowClassName: {
      type: [String, Function],
      default: null
    },
    cellClassName: {
      type: [String, Function],
      default: null
    },
    headerRowClassName: {
      type: [String, Function],
      default: null
    },
    headerCellClassName: {
      type: [String, Function],
      default: null
    },
    showAllOverflow: {
      type: [Boolean, String],
      default: null
    },
    rowKey: {
      type: [String, Number],
      default: 'id'
    },
    autoHeight: {
      type: Boolean,
      default: false
    },
    autoHeightOffset: {
      type: Number,
      default: 82
    },
    treeConfig: {
      type: Object,
      default: null
    },
    groupConfig: {
      type: Object,
      default: null
    },
    checkboxConfig: {
      type: Object,
      default: () => ({})
    },
    emptyText: {
      type: String,
      default: 'No Data Found.'
    },
    defaultSort: {
      type: Object,
      default: null
    },
    filters: {
      type: [Object, Array],
      default: null
    },
    title: {
      type: String,
      default: null
    },
    borderless: {
      type: Boolean,
      default: false
    },
    csvDownload: {
      type: [Boolean, Function],
      default: false
    },
    csvFilename: {
      type: String,
      default: null
    },
    csvConfig: {
      type: Object,
      default: null
    },
    collapsible: {
      type: Boolean,
      default: false
    },
    manageColumns: {
      type: Boolean,
      default: false
    },
    scrollConfig: {
      type: Object,
      default() {
        return {
          enabled: true,
          rowHeight: this.condensed ? 40 : 56,
          buffer: 300,
          dynamicRowHeight: false
        };
      }
    },
    headerProps: {
      type: Object,
      default: null
    },
    tableProps: {
      type: Object,
      default: null
    },
    manageColumnsConfig: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      activeRow: null,
      allTableColumns: [],
      ariaColumnCount: null,
      bodyStyle: {},
      bodyWidth: 0,
      collapsed: false,
      collapseTooltipContent: 'Collapse',
      filterStore: {
        column: null,
        isAllSelected: false,
        isIndeterminate: false,
        multiple: false,
        options: [],
        visible: false
      },
      groupExpandedMap: {},
      hasNestedRows: false,
      horizontalScrollbarHeight: 0,
      hoverColumnHeader: null,
      hoverRow: null,
      id: this._uid,
      isAllSelected: false,
      isAllTreeExpanded: false,
      isIndeterminate: false,
      manageColumnStore: {
        visible: false
      },
      overflowX: false,
      underflowX: false,
      overflowY: false,
      ready: false,
      scrollLeftToRight: false,
      scrollRightToLeft: false,
      scrollStore: {
        buffer: 300,
        dynamicRowHeight: false,
        enabled: true,
        endIndex: 0,
        position: 0,
        rowHeight: 56,
        scrollActive: false,
        scrollHeight: 0,
        scrollLeft: 0,
        scrollTop: 0,
        startIndex: 0,
        pageMode: false,
        stickyElementPositions: {
          title: 0,
          header: 0,
          filters: 0
        }
      },
      selectedMap: {},
      selectRow: null,
      sortStore: {
        activeSort: null,
        column: null
      },
      tableColumns: [],
      tableData: [],
      tableDataHeightMap: [],
      tableRows: [],
      tableStyle: {},
      treeExpandedMap: {},
      treeExpandedChildMap: {},
      verticalScrollbarWidth: 0
    };
  },
  computed: {
    showTableTitle() {
      return Boolean(this.tableTitle || hasSlot(this, 'headerPrefix') || hasSlot(this, 'header') || hasSlot(this, 'headerLeft') || hasSlot(this, 'headerRight') || hasSlot(this, 'headerActions'));
    },
    tableTitle() {
      if (this.headerProps && this.headerProps.title) {
        return this.headerProps.title;
      }
      return this.title;
    },
    tableTitleId() {
      return `pendo-table__title-${this.id}`;
    },
    tableHeight() {
      if (this.tableProps && this.tableProps.height) {
        return this.tableProps.height;
      }
      return this.height;
    },
    tableMaxHeight() {
      if (this.tableProps && this.tableProps.maxHeight) {
        return this.tableProps.maxHeight;
      }
      return this.maxHeight;
    },
    tableDefaultSort() {
      if (this.tableProps && this.tableProps.defaultSort) {
        return this.tableProps.defaultSort;
      }
      return this.defaultSort;
    },
    tableAutoHeight() {
      if (this.tableProps && this.tableProps.autoHeight) {
        return this.tableProps.autoHeight;
      }
      return this.autoHeight;
    },
    tableAutoHeightOffset() {
      if (this.tableProps && this.tableProps.autoHeightOffset) {
        return this.tableProps.autoHeightOffset;
      }
      return this.autoHeightOffset || 0;
    },
    hasFixedColumns() {
      return this.tableColumns.some(column => column.fixed);
    },
    hasDynamicRowHeight() {
      return !this.scrollStore.rowHeight || this.scrollStore.dynamicRowHeight;
    }
  },
  watch: {
    condensed() {
      this.scrollStore.rowHeight = this.condensed ? 40 : 56;
      this.updateScrollHeight();
    },
    data(value) {
      this.reloadData(value || [], false);
    },
    filters: {
      handler() {
        this.clearSelected();
        this.updateActiveTableData();
      },
      deep: true
    },
    columns(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
        return;
      }
      this.reloadColumns(newValue);
    },
    groupConfig: {
      handler(newValue, oldValue) {
        const changedKeys = Object.keys(getShallowObjectDiff(oldValue, newValue));
        if (changedKeys.includes('enabled') || changedKeys.includes('groupKey')) {
          this.reloadData(this.data);
        }
      },
      deep: true
    },
    tableDefaultSort(newValue, oldValue) {
      if (isEqual(newValue, oldValue)) {
        return;
      }
      this.handleDefaultSort();
    },
    status: 'updateTableHeight'
  },
  created() {
    Object.assign(this.scrollStore, this.scrollConfig, {
      startIndex: 0,
      scrollHeight: 0
    });
    this.rowsMap = new Map();
    this.columnsMap = new Map();
    this.tableRows = [];
    this.loadColumns(this.columns);
    this.loadData(this.data);
    if (this.tableDefaultSort) {
      this.handleDefaultSort();
    }
    this.resizeObserver = new ResizeObserver(this.handleResizeEvent);
  },
  mounted: _async(function () {
    const _this = this;
    _this.refreshLayout();
    return _await(_this.$nextTick(), function () {
      _this.ready = true;
      _this.addResizeObservers();
      _this.calculateStickyElementPositions();
    });
  }),
  beforeDestroy() {
    this.hideFilterPanel();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    calculateStickyElementPositions() {
      if (!this.scrollStore.pageMode) {
        return;
      }
      const filterBar = document.querySelector('.pendo-filter-bar');
      let offset = 0;
      if (filterBar) {
        const filterBarPosInScrollTarget = elementPositionInAncestor(filterBar, scrollParent(this.$el));
        const filterBarContentRect = filterBar.getBoundingClientRect();
        const filterBarScrollContainerOffset = filterBarContentRect.top - filterBarPosInScrollTarget.top;
        offset += filterBar.offsetHeight + filterBarScrollContainerOffset;
      }
      this.scrollStore.stickyElementPositions.title = offset;
      const title = this.$el.querySelector('.pendo-table__title');
      if (title) {
        offset += title.offsetHeight;
      }
      this.scrollStore.stickyElementPositions.filters = offset;
      const filters = this.$el.querySelector('.pendo-table__filters');
      if (filters) {
        offset += filters.offsetHeight;
      }
      this.scrollStore.stickyElementPositions.header = offset;
    },
    addResizeObservers() {
      if (this.$el) {
        this.resizeObserver.observe(this.$el);
      }
      if (this.tableAutoHeight) {
        const scrollParent = getScrollParent(this.$el);
        if (scrollParent) {
          this.resizeObserver.observe(scrollParent);
        }
      }
    },
    clearFilter() {
      this.filterStore = {
        isAllSelected: false,
        isIndeterminate: false,
        options: [],
        column: null,
        multiple: false,
        visible: false
      };
      return this.$nextTick();
    },
    clearSelected() {
      this.isAllSelected = false;
      this.isIndeterminate = false;
      this.selectedMap = {};
      emitEvent(this, 'select-clear', [{
        selectedRows: []
      }]);
      return this.$nextTick();
    },
    clearCurrentRow() {
      this.selectRow = null;
      this.hoverRow = null;
      this.hoverColumnHeader = null;
      return this.$nextTick();
    },
    clearScroll() {
      this.scrollStore = _objectSpread2(_objectSpread2({}, this.scrollStore), {}, {
        startIndex: 0,
        scrollTop: 0
      });
      const {
        tableBody
      } = this.$refs;
      if (tableBody && tableBody.$el) {
        tableBody.$el.scrollTop = 0;
        tableBody.$el.scrollLeft = 0;
      }
      return this.$nextTick();
    },
    clearGroupExpand() {
      this.groupExpandedMap = {};
      return this.$nextTick();
    },
    clearTreeExpand() {
      this.treeExpandedMap = {};
      this.treeExpandedChildMap = {};
      this.isAllTreeExpanded = false;
      return this.$nextTick();
    },
    clearAll(clearScroll) {
      this.clearFilter();
      if (clearScroll) {
        this.clearScroll();
      }
      if (!this.checkboxConfig.reserve) {
        this.clearSelected();
      }
      this.clearTreeExpand();
      this.clearGroupExpand();
    },
    loadColumns(columns) {
      if (!columns) {
        return this.$nextTick();
      }
      const tableColumns = cloneDeep(columns).map(column => getColumnConfig(column));
      const actionsColumn = tableColumns.findIndex(column => column.type === 'actions');
      if (actionsColumn !== -1) {
        tableColumns.push(...tableColumns.splice(actionsColumn, 1));
      }
      if (this.manageColumns) {
        this.allTableColumns = tableColumns;
        this.tableColumns = tableColumns.filter(column => column && column.visible);
      } else {
        this.tableColumns = tableColumns;
      }
      this.ariaColumnCount = tableColumns.filter(column => column && column.type !== 'actions').length;
      this.columnsMap.clear();
      let key;
      this.tableColumns.forEach(column => {
        key = column.columnKey;
        this.columnsMap.set(key, column);
      });
      return this.$nextTick();
    },
    loadData: _async(function (data) {
      const _this2 = this;
      if (!data) {
        return _this2.$nextTick();
      }
      let base = data;
      const isRowGroupingEnabled = get(_this2.groupConfig, 'enabled', false);
      if (isRowGroupingEnabled) {
        base = cloneDeep(data);
        const options = {
          childKey: 'children',
          sortChildren: _this2.groupConfig.sortChildren,
          rowKey: _this2.rowKey,
          groupKey: _this2.groupConfig.groupKey,
          groupRowHeight: _this2.groupConfig.groupRowHeight,
          childRowFilter: () => true
        };
        base = groupRows({
          rows: base,
          options
        });
      }
      const {
        rowKey
      } = _this2;
      _this2.rowsMap.clear();
      base.forEach(row => {
        if (!row[rowKey]) {
          warn(`One or more rows does not have the supplied 'rowKey' prop of '${rowKey}'. Each row must have a unique '${rowKey}' key. If the row does not have that key, consider changing the 'rowKey' prop to another unique key the row does have.`, _this2);
          return;
        }
        _this2.rowsMap.set(row[rowKey], row);
      });
      _this2.setGroupExpandedRows();
      return _await(_this2.updateActiveTableData(), function () {
        return _this2.$nextTick();
      });
    }),
    reloadColumns: _async(function (columns) {
      const _this3 = this;
      _this3.clearAll();
      return _await(_this3.loadColumns(columns), function () {
        if (_this3.sortStore.column) {
          const sortedColumn = _this3.tableColumns.find(column => column.property === _this3.sortStore.column.property);
          if (sortedColumn) {
            _this3.setActiveSort(_this3.sortStore.column.property, _this3.sortStore.activeSort);
          } else if (_this3.tableDefaultSort) {
            _this3.handleDefaultSort();
          }
        }
        if (!_this3.sortStore.activeSort && _this3.tableDefaultSort) {
          _this3.handleDefaultSort();
        }
        _this3.updateActiveTableData();
        _this3.refreshLayout();
      });
    }),
    reloadData: _async(function (data, clearScroll) {
      const _this4 = this;
      _this4.clearAll(clearScroll);
      return _await(_this4.loadData(data), function () {
        if (_this4.checkboxConfig.reserve && _this4.getSelectedRows().length) {
          _this4.updateSelectedRows(_this4.getSelectedRows());
        }
      });
    }),
    updateSelectionStatus() {
      const selectedRows = this.getSelectedRows();
      if (!selectedRows.length) {
        this.isAllSelected = false;
        this.isIndeterminate = false;
        return;
      }
      const totalSelectableRows = this.tableData.filter(row => {
        if (this.checkboxConfig.checkMethod) {
          return this.checkboxConfig.checkMethod(row);
        }
        return true;
      });
      this.isAllSelected = selectedRows.length === totalSelectableRows.length;
      this.isIndeterminate = !this.isAllSelected && this.tableData.some(row => this.selectedMap[row[this.rowKey]]);
    },
    updateActiveTableData() {
      const {
        rowKey,
        tableColumns,
        rowsMap,
        filters,
        sortStore,
        treeConfig,
        groupConfig
      } = this;
      let tableData = [...rowsMap.values()];
      const options = {};
      const isRowGroupingEnabled = groupConfig && groupConfig.enabled;
      const isRowTreeEnabled = treeConfig && treeConfig.children;
      if (isRowGroupingEnabled) {
        options.childKey = 'children';
        options.sortChildren = groupConfig.sortChildren;
        options.rowKey = rowKey;
        options.groupKey = groupConfig.groupKey;
        options.groupRowHeight = groupConfig.groupRowHeight;
        options.childRowFilter = row => {
          if (groupConfig.expandable) {
            return this.groupExpandedMap[row[rowKey]];
          }
          return true;
        };
      }
      if (isRowTreeEnabled) {
        options.childKey = treeConfig.children;
        options.sortChildren = treeConfig.sortChildren;
        options.childRowFilter = row => this.treeExpandedMap[row[rowKey]];
      }
      tableData = filterRows({
        rows: tableData,
        columns: tableColumns,
        globalFilters: filters || [],
        options
      });
      if (!this.draggable) {
        tableData = sortRows({
          rows: tableData,
          sortConfig: sortStore,
          options
        });
      }
      if (isRowTreeEnabled || isRowGroupingEnabled) {
        this.hasNestedRows = true;
        tableData = flattenChildRows(tableData, options);
      } else {
        this.hasNestedRows = false;
      }
      if (this.hasDynamicRowHeight) {
        const tableDataHeightMap = [];
        let cummulativeHeight = 0;
        tableData.forEach(row => {
          tableDataHeightMap.push(cummulativeHeight);
          cummulativeHeight += row.height || this.scrollStore.rowHeight;
        });
        this.tableDataHeightMap = tableDataHeightMap;
      }
      this.tableData = tableData;
      emitEvent(this, 'table-data-change', [{
        data: tableData
      }]);
      this.updateScrollHeight();
    },
    updateSelectedRows(selectedRows) {
      const {
        rowsMap,
        rowKey
      } = this;
      if (Array.isArray(selectedRows) && selectedRows.length) {
        const activeRows = selectedRows.filter(selected => rowsMap.has(selected[rowKey]));
        this.selectedMap = keyBy(activeRows, rowKey);
      } else {
        this.selectedMap = {};
      }
      this.updateSelectionStatus();
    },
    getSelectedRows() {
      return Object.values(this.selectedMap);
    },
    getTreeExpandedRows() {
      return Object.values(this.treeExpandedMap);
    },
    downloadTableData() {
      const columns = getCSVColumns(this);
      const filename = getCSVFilename(this);
      const isRowGroupingEnabled = this.groupConfig && this.groupConfig.enabled;
      const isRowTreeEnabled = this.treeConfig && this.treeConfig.children;
      let data = this.tableData;
      if (isRowGroupingEnabled) {
        data = data.filter(row => !row.isGroup);
      }
      if (isRowTreeEnabled) {
        data = flattenChildRows(data, {
          childKey: this.treeConfig.children,
          childRowFilter: row => {
            return !this.treeExpandedMap[row[this.rowKey]];
          }
        });
      }
      if (typeof this.csvDownload === 'function') {
        return this.csvDownload(columns, data);
      }
      const header = columns.map(column => column.label).join(',');
      const csvData = `\ufeff${header}\n${tableToCSV(columns, data)}`;
      return downloadCSV({
        csvData,
        filename
      });
    },
    handleVisibilityChange(isVisible) {
      if (this.ready && isVisible) {
        requestAnimationFrame(() => {
          this.refreshLayout();
        });
      }
    },
    handleResizeEvent(entries) {
      entries.forEach(entry => emitEvent(this, 'resize', [entry]));
      this.refreshLayout();
      this.calculateStickyElementPositions();
    },
    refreshLayout() {
      this.updateColumnsWidth();
      if (!this.collapsed) {
        this.updateTableHeight();
      }
      this.updateScrollHeight();
    },
    scrollToIndex(index) {
      let scroll;
      if (this.hasDynamicRowHeight) {
        scroll = index > 0 ? this.tableDataHeightMap[index] : 0;
      } else {
        scroll = index * this.scrollStore.rowHeight;
      }
      this.scrollToPosition(scroll);
    },
    scrollToPosition(position) {
      const {
        tableBody
      } = this.$refs;
      tableBody.$el.scrollTop = position;
      tableBody.handleScroll({
        target: {
          scrollTop: position
        }
      });
    },
    updateScrollHeight: _async(function () {
      const _this5 = this;
      const scrollHeight = _this5.tableData.reduce((acc, row) => {
        acc += row.height || _this5.scrollStore.rowHeight;
        return acc;
      }, 0);
      if (scrollHeight !== _this5.scrollStore.scrollHeight) {
        _this5.scrollStore.scrollHeight = scrollHeight;
        emitEvent(_this5, 'scroll-height-change', [{
          scrollHeight
        }]);
      }
      const {
        tableBody
      } = _this5.$refs;
      if (!tableBody || !tableBody.$el) {
        return;
      }
      const overflowY = scrollHeight > tableBody.$el.clientHeight;
      return _invoke(function () {
        if (overflowY !== _this5.overflowY) {
          _this5.overflowY = overflowY;
          emitEvent(_this5, 'overflow-y-change', [{
            overflowY
          }]);
          return _await(_this5.$nextTick(), function () {
            _this5.updateColumnsWidth();
          });
        }
      }, function () {
        return _this5.$nextTick();
      });
    }),
    updateColumnsWidth() {
      const {
        fit,
        columnsMap
      } = this;
      const {
        tableBody
      } = this.$refs;
      if (!tableBody || !tableBody.$el) {
        return;
      }
      const bodyElem = tableBody.$el;
      const bodyWidth = bodyElem.getBoundingClientRect().width;
      let totalColumnsWidth = 0;
      let flexColumnsWidth = 0;
      let remainingWidth = bodyWidth;
      const cellMinWidth = 36;
      const fixedRightColumns = [];
      const fixedLeftColumns = [];
      const flexWidthColumns = [];
      columnsMap.forEach((column, key) => {
        switch (column.widthType) {
          case 'fixed':
            {
              const width = parseInt(column.width, 10);
              totalColumnsWidth += width;
              column.renderWidth = width;
              break;
            }
          case 'flex':
            {
              const minWidth = parseInt(column.minWidth, 10);
              flexColumnsWidth += minWidth;
              totalColumnsWidth += minWidth;
              column.renderWidth = minWidth;
              flexWidthColumns.push(column);
              break;
            }
          case 'resize':
            {
              const resizeWidth = parseInt(column.resizeWidth, 10);
              totalColumnsWidth += resizeWidth;
              column.renderWidth = resizeWidth;
              break;
            }
        }
        switch (column.fixed) {
          case 'left':
            fixedLeftColumns.push(column);
            break;
          case 'right':
            fixedRightColumns.push(column);
            break;
        }
        columnsMap.set(key, column);
      });
      remainingWidth -= totalColumnsWidth;
      const verticalScrollbarWidth = this.overflowY ? getScrollbarWidth() : 0;
      this.verticalScrollbarWidth = verticalScrollbarWidth;
      if (flexWidthColumns.length > 0 && fit) {
        if (remainingWidth > verticalScrollbarWidth) {
          this.overflowX = false;
          this.scrollLeftToRight = false;
          this.scrollRightToLeft = false;
          const totalFlexWidth = remainingWidth - verticalScrollbarWidth;
          if (flexWidthColumns.length === 1) {
            const column = flexWidthColumns[0];
            column.renderWidth = (column.minWidth || cellMinWidth) + totalFlexWidth;
            columnsMap.set(column.columnKey, column);
          } else {
            const flexWidthPerPixel = totalFlexWidth / flexColumnsWidth;
            let noneFirstWidth = 0;
            flexWidthColumns.forEach((column, index) => {
              if (index === 0) {
                return;
              }
              const flexWidth = Math.floor((column.minWidth || cellMinWidth) * flexWidthPerPixel);
              noneFirstWidth += flexWidth;
              column.renderWidth = (column.minWidth || cellMinWidth) + flexWidth;
              columnsMap.set(column.columnKey, column);
            });
            const column = flexWidthColumns[0];
            column.renderWidth = (column.minWidth || cellMinWidth) + totalFlexWidth - noneFirstWidth;
            columnsMap.set(column.columnKey, column);
          }
        } else {
          this.overflowX = true;
          flexWidthColumns.forEach(column => {
            column.renderWidth = column.minWidth;
            columnsMap.set(column.columnKey, column);
          });
        }
        this.bodyWidth = Math.max(totalColumnsWidth, bodyWidth - verticalScrollbarWidth);
      } else {
        this.overflowX = totalColumnsWidth > bodyWidth;
        this.bodyWidth = totalColumnsWidth;
      }
      if (this.overflowX) {
        this.underflowX = false;
        this.scrollLeftToRight = bodyElem.scrollLeft > 0;
        this.scrollRightToLeft = bodyElem.clientWidth < bodyElem.scrollWidth - bodyElem.scrollLeft;
      } else {
        let totalColWidth = 0;
        columnsMap.forEach(column => {
          totalColWidth += column.renderWidth;
        });
        this.underflowX = totalColWidth < bodyWidth;
        this.scrollStore.scrollLeft = 0;
      }
      if (fixedLeftColumns.length > 0) {
        fixedLeftColumns.reduce((fixedPositionLeft, column, index) => {
          column.disableReorder = true;
          if (index === fixedLeftColumns.length - 1) {
            column.fixedShadow = true;
          }
          column.fixedPos = fixedPositionLeft;
          columnsMap.set(column.columnKey, column);
          return fixedPositionLeft + column.renderWidth;
        }, 0);
      }
      if (fixedRightColumns.length > 0) {
        fixedRightColumns.reduceRight((fixedPositionRight, column, index) => {
          if (index === fixedRightColumns.length - 1) {
            return fixedPositionRight + column.renderWidth;
          }
          if (index === 0) {
            column.fixedShadow = true;
          }
          column.fixedPos = fixedPositionRight;
          columnsMap.set(column.columnKey, column);
          return fixedPositionRight + column.renderWidth;
        }, 0);
      }
      const updatedColumns = [...columnsMap.values()];
      if (isEqual(this.tableColumns, updatedColumns)) {
        return this.$nextTick();
      }
      this.tableColumns = updatedColumns;
      return this.$nextTick();
    },
    showManageColumnsModal() {
      const usedIndices = [];
      const columns = [];
      this.allTableColumns.forEach((column, index) => {
        if (column.allowReorder && column.visible) {
          usedIndices.push(index);
          columns.push(column);
        }
      });
      const availableColumns = this.allTableColumns.filter(column => column.allowReorder);
      this.manageColumnStore = {
        columns,
        availableColumns,
        usedIndices,
        visible: true
      };
    },
    hideManageColumnsModal(columns) {
      const {
        usedIndices
      } = this.manageColumnStore;
      const allTableColumns = cloneDeep(this.allTableColumns);
      allTableColumns.forEach(column => {
        if (column.allowReorder && column.visible) {
          column.visible = false;
        }
      });
      columns.forEach((column, index) => {
        const newIndex = usedIndices[index];
        const oldIndex = allTableColumns.findIndex(col => col.property === column.property);
        allTableColumns[oldIndex].visible = true;
        if (newIndex == null) {
          allTableColumns.push(...allTableColumns.splice(oldIndex, 1));
        } else {
          allTableColumns.splice(newIndex, 0, ...allTableColumns.splice(oldIndex, 1));
        }
      });
      if (isEqual(this.allTableColumns, allTableColumns)) {
        return this.$nextTick();
      }
      this.reloadColumns(allTableColumns);
      const visibleColumns = this.tableColumns.reduce((columns, column) => {
        if (column.property) {
          columns.push(column.property);
        }
        return columns;
      }, []);
      emitEvent(this, 'column-change', [{
        columns: visibleColumns
      }]);
      return this.$nextTick();
    },
    collapseTable() {
      requestAnimationFrame(() => {
        this.collapsed = true;
        this.collapseTooltipContent = 'Expand';
        emitEvent(this, 'collapse-change', [{
          collapsed: this.collapsed
        }]);
      });
    },
    expandTable() {
      requestAnimationFrame(() => {
        this.collapsed = false;
        this.collapseTooltipContent = 'Collapse';
        emitEvent(this, 'collapse-change', [{
          collapsed: this.collapsed
        }]);
      });
    },
    toggleTableCollapse() {
      this.collapseTooltipContent = null;
      if (this.collapsed) {
        return this.expandTable();
      }
      this.collapseTable();
    },
    toggleColumnHeaderHover(column, colIndex) {
      if (!colIndex && colIndex !== 0) {
        this.hoverColumnHeader = null;
        return;
      }
      if (!column.sortable && !column.filters.length) {
        return;
      }
      requestAnimationFrame(() => {
        this.hoverColumnHeader = colIndex;
      });
    },
    toggleRowActive(row) {
      requestAnimationFrame(() => {
        this.activeRow = row[this.rowKey];
      });
    },
    clearRowHover() {
      const hoveredRows = this.$el.querySelectorAll('.pendo-table__row.is-hover');
      if (hoveredRows.length) {
        hoveredRows.forEach(elem => {
          removeClass(elem, 'is-hover');
        });
      }
      this.hoverRow = null;
      emitEvent(this, 'row-hover-clear');
    },
    setRowHover(row, index) {
      if (!this.highlightHoverRow || this.scrollStore.scrollActive || this.hoverRow === row[this.rowKey]) {
        return;
      }
      this.clearRowHover();
      addClass(this.$el.querySelector(`.pendo-table__row[data-uid="${index}"]`), 'is-hover');
      this.hoverRow = row[this.rowKey];
      emitEvent(this, 'row-hover-change', {
        row
      });
    },
    toggleAllRowsSelect(value) {
      if (value && this.checkboxConfig.selectAll !== false) {
        const selectableRows = this.tableData.filter(row => {
          if (row.isGroup) {
            return false;
          }
          if (this.checkboxConfig.checkMethod) {
            return this.checkboxConfig.checkMethod(row);
          }
          return true;
        });
        this.selectedMap = keyBy(selectableRows, this.rowKey);
        emitEvent(this, 'select-all', [{
          selectedRows: this.getSelectedRows()
        }]);
      } else {
        this.clearSelected();
      }
      this.updateSelectionStatus();
      emitEvent(this, 'select-change', [{
        selectedRows: this.getSelectedRows()
      }]);
    },
    toggleRowSelect(_ref) {
      let {
        row
      } = _ref;
      if (this.selectedMap[row[this.rowKey]]) {
        this.$delete(this.selectedMap, row[this.rowKey]);
        emitEvent(this, 'select-remove', [{
          row,
          checked: false
        }]);
      } else {
        this.$set(this.selectedMap, row[this.rowKey], row);
        emitEvent(this, 'select-add', [{
          row,
          checked: true
        }]);
      }
      this.updateSelectionStatus();
      emitEvent(this, 'select-change', [{
        row,
        selectedRows: this.getSelectedRows()
      }]);
    },
    toggleRowGroupExpand(row) {
      row = this.rowsMap.get(row[this.rowKey]);
      if (this.groupExpandedMap[row[this.rowKey]]) {
        this.$delete(this.groupExpandedMap, row[this.rowKey]);
      } else {
        this.$set(this.groupExpandedMap, row[this.rowKey], row);
      }
      emitEvent(this, 'group-expand-change', [{
        row,
        groupExpandedKeys: Object.keys(this.groupExpandedMap)
      }]);
      this.updateActiveTableData();
      return this.$nextTick();
    },
    toggleRowTreeExpand(row) {
      row = this.rowsMap.get(row[this.rowKey]);
      if (this.treeExpandedMap[row[this.rowKey]]) {
        this.$delete(this.treeExpandedMap, row[this.rowKey]);
        row[this.treeConfig.children].forEach(childRow => {
          this.$delete(this.treeExpandedChildMap, childRow[this.rowKey]);
        });
      } else {
        this.$set(this.treeExpandedMap, row[this.rowKey], row);
        row[this.treeConfig.children].forEach(childRow => {
          this.$set(this.treeExpandedChildMap, childRow[this.rowKey], childRow);
        });
      }
      emitEvent(this, 'tree-expand-change', [{
        row,
        treeExpandedRows: this.getTreeExpandedRows()
      }]);
      this.updateActiveTableData();
      this.updateTreeExpandStatus();
      return this.$nextTick();
    },
    toggleAllRowsTreeExpand() {
      if (this.treeConfig.expandAll === false) {
        return;
      }
      if (!this.isAllTreeExpanded) {
        const treeExpandableRows = this.tableData.filter(row => row[this.treeConfig.children]);
        this.treeExpandedMap = keyBy(treeExpandableRows, this.rowKey);
        this.treeExpandedChildMap = treeExpandableRows.reduce((acc, row) => {
          row[this.treeConfig.children].forEach(childRow => {
            acc[childRow[this.rowKey]] = childRow;
          });
          return acc;
        }, {});
      } else {
        this.clearTreeExpand();
      }
      emitEvent(this, 'tree-expand-change', [{
        treeExpandedRows: this.getTreeExpandedRows()
      }]);
      this.updateActiveTableData();
      this.updateTreeExpandStatus();
      return this.$nextTick();
    },
    updateTreeExpandStatus() {
      const treeExpandedRows = this.getTreeExpandedRows();
      if (!treeExpandedRows.length) {
        this.isAllTreeExpanded = false;
        return;
      }
      const totalTreeExpandableRows = this.tableData.filter(row => row[this.treeConfig.children]);
      this.isAllTreeExpanded = treeExpandedRows.length === totalTreeExpandableRows.length;
    },
    handleCellClick(event, _ref2) {
      let {
        cell,
        column,
        row
      } = _ref2;
      if (this.highlightCurrentRow) {
        requestAnimationFrame(() => {
          this.selectRow = row;
        });
      }
      emitEvent(this, 'cell-click', [{
        cell,
        column,
        event,
        row
      }]);
    },
    handleDefaultSort() {
      const {
        prop,
        order
      } = this.tableDefaultSort;
      const sortedColumn = this.tableColumns.find(column => column.property === prop);
      if (!sortedColumn) {
        return;
      }
      const {
        property
      } = sortedColumn;
      let sortOrder = 'none';
      if (['asc', 'ascending'].includes(order)) {
        sortOrder = 'ascending';
      }
      if (['desc', 'descending'].includes(order)) {
        sortOrder = 'descending';
      }
      this.setActiveSort(property, sortOrder);
    },
    setGroupExpandedRows() {
      const isRowGroupingEnabled = get(this.groupConfig, 'enabled', false);
      const expandedGroupKeys = get(this.groupConfig, 'expandedKeys');
      if (isRowGroupingEnabled && expandedGroupKeys) {
        expandedGroupKeys.forEach(key => {
          const row = this.rowsMap.get(key);
          if (row) {
            this.$set(this.groupExpandedMap, key, row);
          }
        });
      }
      return this.$nextTick();
    },
    setActiveSort(prop, order) {
      const {
        tableColumns,
        sortStore
      } = this;
      const column = tableColumns.find(col => col.property === prop);
      if (sortStore.column && column.property !== sortStore.column.property) {
        this.sortStore = {
          column: null,
          activeSort: 'none'
        };
      }
      if (!order) {
        order = {
          none: 'ascending',
          ascending: 'descending',
          descending: 'none'
        }[this.sortStore.activeSort || 'none'];
      }
      if (this.sortStore.activeSort !== order) {
        this.sortStore = {
          column: _objectSpread2(_objectSpread2({}, column), {}, {
            order
          }),
          activeSort: order
        };
        if (column.sortable !== 'custom') {
          this.updateActiveTableData();
        }
        emitEvent(this, 'sort-change', [{
          column,
          prop,
          order
        }]);
      }
      return this.$nextTick();
    },
    filterColumn(_ref3) {
      let {
        column,
        appliedFilters
      } = _ref3;
      const updatedFilters = this.filterStore.options.map(option => {
        option.checked = appliedFilters.includes(option.value);
        return option;
      });
      this.filterStore.options = updatedFilters;
      this.updateActiveTableData();
      emitEvent(this, 'filter-change', [{
        column,
        prop: column.property,
        values: this.filterStore.options
      }]);
    },
    showFilterPanel(column) {
      const isAllSelected = column.filters.every(_ref4 => {
        let {
          checked
        } = _ref4;
        return checked;
      });
      const isIndeterminate = !isAllSelected && column.filters.some(_ref5 => {
        let {
          checked
        } = _ref5;
        return checked;
      });
      this.filterStore = {
        column,
        isAllSelected,
        isIndeterminate,
        multiple: column.filterMultiple,
        options: column.filters,
        visible: true
      };
    },
    hideFilterPanel() {
      this.filterStore = _objectSpread2(_objectSpread2({}, this.filterStore), {}, {
        isAllSelected: false,
        isIndeterminate: false,
        options: [],
        visible: false
      });
      return this.$nextTick();
    },
    updateTableHeight() {
      const {
        title,
        filters,
        tableHeader
      } = this.$refs;
      let nonBodyHeight = 0;
      const fixedHeight = parseInt(this.tableHeight, 10);
      const maxHeight = parseInt(this.tableMaxHeight, 10);
      if (title && title.$el) {
        nonBodyHeight += title.$el.offsetHeight;
      }
      if (filters) {
        nonBodyHeight += filters.offsetHeight;
      }
      if (tableHeader && tableHeader.$el) {
        nonBodyHeight += tableHeader.$el.offsetHeight;
      }
      if (!fixedHeight && !maxHeight || this.tableAutoHeight) {
        if (!fixedHeight && !maxHeight && !this.tableAutoHeight) {
          warn('missing height/maxHeight/autoHeight property. Table height rendering may be negatively impacted.', this);
        }
        const tableScrollParent = scrollParent(this.$el) || document.documentElement;
        const {
          top
        } = this.$el.getBoundingClientRect();
        const topRelativeToViewport = top + tableScrollParent.scrollTop;
        const componentHeight = window.innerHeight - topRelativeToViewport - this.tableAutoHeightOffset;
        const bodyHeight = componentHeight - nonBodyHeight;
        if (this.scrollStore.pageMode) {
          if (!this.status || this.status === 'resolved') {
            this.bodyStyle = {
              maxHeight: '100%'
            };
            this.tableStyle = {
              maxHeight: '100%'
            };
          } else {
            this.bodyStyle = {
              height: `${bodyHeight}px`
            };
            this.tableStyle = {
              height: `${componentHeight}px`
            };
          }
          return this.$nextTick();
        }
        this.bodyStyle = {
          maxHeight: `${bodyHeight}px`
        };
        this.tableStyle = {
          maxHeight: `${componentHeight}px`
        };
        return this.$nextTick();
      }
      if (fixedHeight) {
        this.bodyStyle = {
          height: `${fixedHeight - nonBodyHeight}px`
        };
        this.tableStyle = {
          height: `${this.collapsed ? nonBodyHeight : fixedHeight}px`
        };
        return this.$nextTick();
      }
      if (maxHeight) {
        this.bodyStyle = {
          maxHeight: `${maxHeight - nonBodyHeight}px`
        };
        this.tableStyle = {
          maxHeight: `${maxHeight}px`
        };
      }
      return this.$nextTick();
    },
    addRow(row) {
      if (findRowLocation(row, this)) {
        warn(`row with ${this.rowKey} ${row[this.rowKey]} already exists.`, this);
        return;
      }
      this.rowsMap.set(row[this.rowKey], row);
      this.updateActiveTableData();
    },
    updateRow(row) {
      const rowLocation = findRowLocation(row, this);
      if (!rowLocation) {
        warn(`cannot find row with ${this.rowKey} ${row[this.rowKey]}.`, this);
        return;
      }
      if (rowLocation.rowSet) {
        rowLocation.rowSet[rowLocation.targetRowIndex] = row;
      } else {
        this.rowsMap.set(rowLocation.key, row);
      }
      this.updateActiveTableData();
    },
    updateRows(rows) {
      let hasUpdatedRows = false;
      rows.forEach(row => {
        const rowLocation = findRowLocation(row, this);
        if (!rowLocation) {
          warn(`cannot find row with ${this.rowKey} ${row[this.rowKey]}.`, this);
          return;
        }
        if (rowLocation.rowSet) {
          rowLocation.rowSet[rowLocation.targetRowIndex] = row;
        } else {
          this.rowsMap.set(rowLocation.key, row);
        }
        hasUpdatedRows = true;
      });
      if (hasUpdatedRows) {
        this.updateActiveTableData();
      }
    },
    removeRow(row) {
      const rowLocation = findRowLocation(row, this);
      if (!rowLocation) {
        return;
      }
      if (rowLocation.rowSet) {
        rowLocation.rowSet.splice(rowLocation.targetRowIndex, 1);
      } else {
        this.rowsMap.delete(rowLocation.key);
      }
      this.$delete(this.selectedMap, row[this.rowKey]);
      this.updateActiveTableData();
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-table.vue2.js.map
